import React, { Fragment, useCallback, useEffect } from "react";
import { Container } from "../components";
import { PartnerOffering } from "../data/partner_offering";
import { useQueryString } from "../hooks";
import { Locale } from "../lib/i18n/i18n_provider";
import { useEnglishLocationName } from "../offering/api";
import { Spacer } from "../offering/spacer";
import { OfferingParams } from "../offering/template_offering_checkout_common";
import { useAnalytics } from "../lib/analytics/analytics";
import { useIsWework } from "../offering/hooks/use_is_wework";
import { isWeworkAllAccessUrl } from "../offering/wework/wework_unlimited_access";

interface PromoCodeBoxProps {
  partnerOffering: PartnerOffering;
  spaceID: string | undefined;
  locale: Locale;
}

const PromoCodeBox = (props: PromoCodeBoxProps) => {
  // Match the per-location entry only on a product page, where a space is
  // selected. The listing/category surface passes no spaceID, so it has no
  // per-location entry and falls through to the offering-wide content below.
  const matchingOfferingLocation = props.spaceID
    ? props.partnerOffering?.offeringLocations.find(
        (ol) => ol.spaceID === props.spaceID
      )
    : undefined;
  // The backend resolves each location entry's content (its own, the inherited
  // offering-wide content, or empty when the location opts out of inheriting),
  // so a matched entry is authoritative — never fall back to the offering-wide
  // value on a product page, or an opted-out location would leak it.
  const promoCodeBoxContent =
    (matchingOfferingLocation
      ? matchingOfferingLocation.promoCodeBoxContent
      : props.partnerOffering?.promoCodeBoxContent) || "";
  const queryString = useQueryString<OfferingParams>();
  const isWeWork = useIsWework();
  /** This function to find the URL in the Promo Content
   * and add additional params (fromCatalog) to the existed URL
   */
  const contentClickHandler = useCallback(
    (e: any) => {
      const targetLink = e.target.closest("a");
      if (!targetLink) return;
      // WeWork (Israel) only: let the browser handle target="_blank" links so
      // data-configured external upsells (e.g. WeWork All Access) open in a new
      // tab. Other partners keep the original same-tab + fromCatalog behavior.
      if (isWeWork && targetLink.target === "_blank") return;
      e.preventDefault();
      const queryParams = new URLSearchParams();
      if (queryString.fromCatalog) {
        queryParams.append("fromCatalog", queryString.fromCatalog);
      }
      window.location.assign(`${targetLink.href}?${queryParams}`);
    },
    [queryString.fromCatalog, isWeWork]
  );

  const analytics = useAnalytics();

  // Skip the en-US side-fetch unless this promo block actually renders the
  // WeWork "Unlimited access" upsell — otherwise every WeWork details
  // template (day_office, meeting_room, etc.) would pay an extra HTTP RTT
  // for an event that can never fire there.
  const hasUnlimitedAccessUpsell =
    isWeWork && promoCodeBoxContent.includes("all-access");
  // Forced English name keeps WeWork's GA4 `location_name` custom dimension
  // consistent regardless of the visitor's UI locale (he-IL vs en-US).
  const englishLocationName = useEnglishLocationName(
    matchingOfferingLocation?.location?.id,
    hasUnlimitedAccessUpsell
  );
  // Hebrew localized name is the fallback if the en-US fetch hasn't resolved
  // by the time the user clicks — better an attributable value than empty.
  const fallbackLocationName = matchingOfferingLocation?.location?.name ?? "";
  const onLinkClick = useCallback(
    (e: MouseEvent) => {
      // Right-click (button 2) opens the context menu — copying the URL
      // is not a navigation, so don't count it. Left (0) and middle (1)
      // both navigate.
      if (e.button !== 0 && e.button !== 1) return;
      // currentTarget is the <a> the listener was bound to. e.target can
      // be a nested child (a <span>/<strong>/<img> inside the anchor),
      // whose .href is undefined.
      const anchor = e.currentTarget as HTMLAnchorElement | null;
      const href = anchor?.href ?? "";
      analytics.event(
        "Promotion Box Link Click",
        {
          "Promotion Call Box": href,
          Source: "WL",
        },
        { onlyProviders: ["mixpanel", "customerio"] }
      );
      // WeWork (Israel): the daily-access promo box renders an "Unlimited
      // access" upsell anchor to wework.co.il/.../all-access. Mirror the
      // dropdown's `unlimited_access_click` event so their GTM container can
      // attribute clicks from either surface.
      if (isWeWork && isWeworkAllAccessUrl(href)) {
        analytics.event(
          "unlimited_access_click",
          {
            surface: "grey_box",
            location_name: englishLocationName ?? fallbackLocationName,
          },
          { onlyProviders: ["gtm"] }
        );
      }
    },
    [analytics, englishLocationName, fallbackLocationName, isWeWork]
  );

  useEffect(() => {
    if (!promoCodeBoxContent) {
      return;
    }

    const containerEl = document.getElementById(
      "template-offering-details-base-offering-promo"
    );

    if (!containerEl) return;

    const linkEl = containerEl.querySelector("a");

    if (!linkEl) return;

    // mousedown (vs click) lets us catch middle-click "open in new tab",
    // which doesn't fire `click`. Right-click is filtered in the handler.
    linkEl.addEventListener("mousedown", onLinkClick);
    return () => {
      linkEl.removeEventListener("mousedown", onLinkClick);
    };
  }, [onLinkClick, promoCodeBoxContent]);

  if (!promoCodeBoxContent) {
    return null;
  }

  return (
    <Fragment>
      <Spacer size={16} />
      <div
        className={`template-offering-details-base-offering-promo text text-size-md text-${props.locale.baseLanguageTag}`}
        id={"template-offering-details-base-offering-promo"}
      >
        <Container paddingVertical={8} paddingHorizontal={16}>
          <div
            onClick={contentClickHandler}
            dangerouslySetInnerHTML={{
              __html: promoCodeBoxContent,
            }}
          />
        </Container>
      </div>
    </Fragment>
  );
};

export default PromoCodeBox;
